import { _ } from "../../../core/polyglot/polyglot.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import AccountFormulaToken from "../../../models/keyFigure/formulaToken/AccountFormulaToken.js";
import AccountGroupFormulaToken from "../../../models/keyFigure/formulaToken/AccountGroupFormulaToken.js";
import AccountIntervalFormulaToken from "../../../models/keyFigure/formulaToken/AccountIntervalFormulaToken.js";
import ActionButtonWidget from "../../../core/widgets/ActionButtonWidget/ActionButtonWidget.js";
import AdditionFormulaToken from "../../../models/keyFigure/formulaToken/AdditionFormulaToken.js";
import ClosingParenthesisFormulaToken from "../../../models/keyFigure/formulaToken/ClosingParenthesisFormulaToken.js";
import CollapsedActionsBlockWidget from "../../../core/chrome/CollapsedActionsBlockWidget.js";
import ConstantFormulaToken from "../../../models/keyFigure/formulaToken/ConstantFormulaToken.js";
import DivisionFormulaToken from "../../../models/keyFigure/formulaToken/DivisionFormulaToken.js";
import FormulaTokenLabelRenderer from "./visitors/FormulaTokenLabelRenderer.js";
import KeyFigureFormulaToken from "../../../models/keyFigure/formulaToken/KeyFigureFormulaToken.js";
import MultiplicationFormulaToken from "../../../models/keyFigure/formulaToken/MultiplicationFormulaToken.js";
import OpeningParenthesisFormulaToken from "../../../models/keyFigure/formulaToken/OpeningParenthesisFormulaToken.js";
import SubtractionFormulaToken from "../../../models/keyFigure/formulaToken/SubtractionFormulaToken.js";

/**
 * KeyFigureActionsWidget renders right-hand menu actions for actions related to the edited key figure in the
 * KeyFigureEditorDocument.
 *
 * @param {keyFigures[]} spec.keyFigures - List of all available keyFigures.
 * @param {KeyFigure} spec.keyFigure - Currently edited KeyFigure.
 * @param {Company} spec.currentCompany - Company that the current user belongs to.
 */
export default class KeyFigureActionsWidget extends Widget {
	constructor({
		keyFigures,
		keyFigure,
		currentCompany,
		isAccountGroupsUsageInKpiEditorEnabled,
	}) {
		super(...arguments);

		this._keyFigures = keyFigures;
		this._keyFigure = keyFigure;
		this._currentCompany = currentCompany;

		this._isAccountGroupsUsageInKpiEditorEnabled =
			isAccountGroupsUsageInKpiEditorEnabled;

		this.saveRequested = this._events.createEvent();
		this.saveAsRequested = this._events.createEvent();
		this.removeRequested = this._events.createEvent();
		this.downloadRequested = this._events.createEvent();
		this.addTokenRequested = this._events.createEvent();

		this._collapsedReportActionsWidget = new CollapsedActionsBlockWidget({
			content: [
				this._renderDownloadOn.bind(this),
				this._renderRevertOrDeleteOn.bind(this),
			],
		});
	}

	setKeyFigures(keyFigures) {
		this._keyFigures = keyFigures;
		this.update();
	}

	setKeyFigure(keyFigure) {
		this._keyFigure = keyFigure;
		this.update();
	}

	renderContentOn(html) {
		this._renderReportActionsOn(html);
		this._renderAddFormulaTokenActionsOn(html);
	}

	_renderReportActionsOn(html) {
		html.render(
			this._rightMenuSection("", [
				this._renderSaveOn.bind(this),
				this._renderSaveAsOn.bind(this),
				this._collapsedReportActionsWidget,
			]),
		);
	}

	_renderDownloadOn(html) {
		html.render(
			new ActionButtonWidget({
				label: _("Download"),
				click: () => this.downloadRequested.trigger(),
			}),
		);
	}

	_renderAddFormulaTokenActionsOn(html) {
		if (this._keyFigure.isNonFinancialKeyFigure()) {
			return;
		}

		html.render(
			this._rightMenuSection(
				_("Add component"),
				this._renderAddComponentOn.bind(this),
				"add-component",
			),
			this._rightMenuSection(
				_("Add operator"),
				this._renderAddOperatorOn.bind(this),
				"add-operator",
			),
			this._rightMenuSection(
				_("Add parenthesis"),
				this._renderAddParenthesisOn.bind(this),
				"add-parenthesis",
			),
		);
	}

	_renderSaveOn(html) {
		html.render(
			new ActionButtonWidget({
				label: _("Save key figure"),
				isPrimary: true,
				dataTestName: "save-keyfigure",
				click: () => this.saveRequested.trigger(),
			}),
		);
	}

	_renderSaveAsOn(html) {
		if (this._keyFigure.isNew()) {
			return;
		}

		html.render(
			new ActionButtonWidget({
				label: _("Save as..."),
				dataTestName: "save-as",
				click: () => this.saveAsRequested.trigger(),
			}),
		);
	}

	_renderRevertOrDeleteOn(html) {
		const keyFigure = this._keyFigure;
		if (
			keyFigure &&
			!(
				keyFigure.canBeDeletedBy(this._currentCompany) ||
				keyFigure.canBeRestoredFor(this._currentCompany)
			)
		) {
			return;
		}

		html.render(
			new ActionButtonWidget({
				label: keyFigure.canBeRestoredFor(this._currentCompany)
					? _("Revert")
					: _("Delete"),
				isPrimary: false,
				dataTestName: "delete-keyfigure",
				click: () => {
					this._deleteKeyfigure();
				},
			}),
		);
	}

	_renderAddComponentOn(html) {
		const isAccountGroupsUsageInKpiEditorEnabled =
			this._isAccountGroupsUsageInKpiEditorEnabled;

		html.render(
			[
				AccountFormulaToken,
				AccountIntervalFormulaToken,
				KeyFigureFormulaToken,
				ConstantFormulaToken,
				AccountGroupFormulaToken,
			]
				.filter((tokenClass) =>
					tokenClass.isVisible({
						isAccountGroupsUsageInKpiEditorEnabled,
					}),
				)
				.sort((a, b) => (a.getLabel() < b.getLabel() ? -1 : 1))
				.map(this._addComponentButtonRenderer.bind(this)),
		);
	}

	_addComponentButtonRenderer(tokenClass) {
		return (html) => {
			html.render(
				new ActionButtonWidget({
					label: tokenClass.getLabel(),
					dataTestName: `choose-${tokenClass.getType()}`,
					click: () => {
						const context = { keyFigures: this._keyFigures };
						const token = tokenClass.newDefault(context);
						this.addTokenRequested.trigger(token);
					},
				}),
			);
		};
	}

	_renderAddOperatorOn(html) {
		html.div(
			{ klass: "btn-group" },
			[
				new AdditionFormulaToken(),
				new SubtractionFormulaToken(),
				new MultiplicationFormulaToken(),
				new DivisionFormulaToken(),
			].map(this._makeButtonFromToken.bind(this)),
		);
	}

	_renderAddParenthesisOn(html) {
		html.div(
			{ klass: "btn-group" },
			[
				new OpeningParenthesisFormulaToken(),
				new ClosingParenthesisFormulaToken(),
			].map(this._makeButtonFromToken.bind(this)),
		);
	}

	_makeButtonFromToken(token) {
		return (html) => {
			html.button(
				{
					klass: "btn btn-default btn-primary",
					"data-test": `choose-${token.getType()}`,
					click: () => this.addTokenRequested.trigger(token),
				},
				new FormulaTokenLabelRenderer().visit(token),
			);
		};
	}

	_rightMenuSection(label, contents, dataTestName) {
		return (html) =>
			html.div(
				{ klass: "right-menu-section", "data-test": dataTestName },
				[label, contents],
			);
	}

	_deleteKeyfigure() {
		this._confirmDeletion({
			name: this._keyFigure.getName(),
			callback: () => this.removeRequested.trigger(),
		});
	}
}
